import React, { useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

/**
 * VehicleChainsTable — wide horizontal-timeline table showing each physical
 * vehicle's day plan.  Columns are shift+direction slots (A-LOGIN, B-LOGIN,
 * A-LOGOUT, ...) sorted chronologically, with an "empty leg" column between
 * consecutive slots.  Each cell shows either the trip (time + km) or the
 * empty-leg breakdown (deadhead + idle).
 *
 * Data source: `optimizationResults.results[].vehicle_schedule.vehicles[]`
 * where each vehicle carries a `chain_detail` array of alternating trip /
 * empty_leg entries (see backend `services/optimization.py::compute_vehicle_schedule`).
 */

// Fixed widths for the sticky-left columns so the CSS `left: N` offsets
// on the second/third sticky cells match the actual rendered widths.
// Tuned to fit the widest expected content (V=`B99-V9`, Bus=`B99 (40)`).
const W_NUM = 48;      // #
const W_VEHICLE = 96;  // Vehicle
const W_BUS = 116;     // Bus (capacity, incl. right-size arrow e.g. `B99 (40→13)`)
const OFFSET_VEHICLE = W_NUM;
const OFFSET_BUS = W_NUM + W_VEHICLE;

export default function VehicleChainsTable({ open, onOpenChange, optimizationResults }) {
  const { columnOrder, vehicles, summary } = useMemo(
    () => buildTableModel(optimizationResults),
    [optimizationResults]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[92vh] overflow-auto p-0">
        <DialogHeader className="sticky top-0 bg-white z-10 border-b p-4">
          <DialogTitle className="text-base">
            Vehicle Chains
            <span
              className="ml-3 font-mono text-xs bg-slate-100 px-2 py-0.5 rounded font-normal align-middle"
              data-testid="chains-vehicle-count"
            >
              {vehicles.length} vehicles
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Per-vehicle day plan: chronological shift and direction trips with empty-leg gaps between them.
          </DialogDescription>
          {summary && (
            <div
              className="text-[11px] text-slate-600 mt-1 flex flex-wrap gap-x-4 gap-y-1"
              data-testid="chains-summary"
            >
              <span>Total trips: <b>{summary.total_trips}</b></span>
              <span>Aggregate dead-leg: <b>{fmtMin(summary.total_deadhead_min)}</b> · <b>{summary.total_deadhead_km.toFixed(1)} km</b></span>
              <span>Aggregate idle (all vehicles): <b>{fmtMin(summary.total_idle_min)}</b></span>
              <span>Single-trip vehicles: <b>{summary.single_trip_vehicles}</b></span>
            </div>
          )}
        </DialogHeader>

        <div className="p-4">
          {vehicles.length === 0 ? (
            <div className="text-sm text-slate-500 py-12 text-center">
              No vehicles to display. Run an optimization first.
            </div>
          ) : (
            <div className="overflow-x-auto border rounded-md" data-testid="vehicle-chains-table-wrap">
              <table className="text-xs border-separate border-spacing-0" data-testid="vehicle-chains-table">
                <colgroup>
                  <col style={{ width: `${W_NUM}px` }} />
                  <col style={{ width: `${W_VEHICLE}px` }} />
                  <col style={{ width: `${W_BUS}px` }} />
                </colgroup>
                <thead className="bg-slate-50">
                  <tr>
                    <StickyHead width={W_NUM} left={0}>#</StickyHead>
                    <StickyHead width={W_VEHICLE} left={OFFSET_VEHICLE}>Vehicle</StickyHead>
                    <StickyHead width={W_BUS} left={OFFSET_BUS}>Bus</StickyHead>
                    {columnOrder.map((col) =>
                      col.type === "trip" ? (
                        <th
                          key={col.key}
                          className="px-2 py-2 text-center bg-slate-100 border-l border-b border-slate-200 font-semibold whitespace-nowrap min-w-[110px]"
                        >
                          {col.label}
                        </th>
                      ) : (
                        <th
                          key={col.key}
                          className="px-2 py-2 text-center bg-white border-l border-b border-slate-200 font-mono text-slate-400 text-[10px] min-w-[80px]"
                        >
                          →
                        </th>
                      )
                    )}
                    <th className="px-2 py-2 text-left bg-slate-100 border-l border-b border-slate-200 sticky right-0 z-[2] min-w-[180px]">
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v, idx) => (
                    <tr key={v.vehicle_id} className="group hover:bg-slate-50">
                      <StickyCell width={W_NUM} left={0} mono>{idx + 1}</StickyCell>
                      <StickyCell width={W_VEHICLE} left={OFFSET_VEHICLE} mono bold data-testid={`vc-vehicle-${v.vehicle_id}`}>
                        {v.vehicle_id}
                      </StickyCell>
                      <StickyCell width={W_BUS} left={OFFSET_BUS} mono>
                        {v.original_capacity && v.original_capacity !== v.capacity ? (
                          <span
                            className="text-emerald-700"
                            title={`Right-sized: max ${v.required_pax} pax on board`}
                            data-testid={`vc-rightsized-${v.vehicle_id}`}
                          >
                            {v.bus_id} ({v.original_capacity}→{v.capacity})
                          </span>
                        ) : (
                          <>{v.bus_id} ({v.capacity})</>
                        )}
                      </StickyCell>
                      {columnOrder.map((col) => {
                        if (col.type === "trip") {
                          const trip = v.by_slot[col.key];
                          return (
                            <td key={col.key} className="px-2 py-2 text-center border-l border-b border-slate-200 align-top min-w-[110px]">
                              {trip ? (
                                <div className="whitespace-nowrap">
                                  <div className="font-mono text-[11px]">
                                    {trip.start_hhmm}–{trip.end_hhmm}
                                  </div>
                                  <div className="text-[10px] text-slate-500">
                                    {trip.route_km} km · {trip.passengers} pax
                                  </div>
                                </div>
                              ) : (
                                <span className="text-slate-300">—</span>
                              )}
                            </td>
                          );
                        }
                        const gap = v.by_gap[col.key];
                        return (
                          <td key={col.key} className="px-2 py-2 text-center border-l border-b border-slate-200 align-top bg-slate-50/40 min-w-[80px]">
                            {gap ? (
                              <div className="whitespace-nowrap text-[10px]">
                                <div className="font-mono text-amber-700">{fmtMin(gap.deadhead_min)} · {gap.deadhead_km} km</div>
                                {gap.idle_min > 0 && (
                                  <div className="text-slate-500">{fmtMin(gap.idle_min)} idle</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-2 border-l border-b border-slate-200 sticky right-0 z-[1] bg-white group-hover:bg-slate-50 whitespace-nowrap min-w-[180px]">
                        <VehicleNotes vehicle={v} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- helpers ---------- */

function StickyHead({ children, width, left = 0 }) {
  return (
    <th
      className="px-3 py-2 text-left bg-slate-100 sticky z-[2] border-r border-b border-slate-200 whitespace-nowrap"
      style={{ left, width, minWidth: width, maxWidth: width }}
    >
      {children}
    </th>
  );
}
function StickyCell({ children, width, left = 0, mono, bold, ...rest }) {
  return (
    <td
      className={
        "px-3 py-2 bg-white group-hover:bg-slate-50 sticky z-[1] border-r border-b border-slate-200 whitespace-nowrap overflow-hidden text-ellipsis " +
        (mono ? "font-mono " : "") +
        (bold ? "font-semibold " : "")
      }
      style={{ left, width, minWidth: width, maxWidth: width }}
      {...rest}
    >
      {children}
    </td>
  );
}

function fmtMin(m) {
  if (m == null || Number.isNaN(m)) return "—";
  m = Math.round(m);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm === 0 ? `${h}h` : `${h}h ${mm}m`;
}

function VehicleNotes({ vehicle }) {
  const trips = vehicle.chain_detail.filter((c) => c.kind === "trip");
  const gaps = vehicle.chain_detail.filter((c) => c.kind === "empty_leg");
  const resized = vehicle.original_capacity && vehicle.original_capacity !== vehicle.capacity ? (
    <div className="text-emerald-700">
      Right-sized {vehicle.original_capacity}s → {vehicle.capacity}s (max {vehicle.required_pax} pax)
    </div>
  ) : null;
  if (trips.length === 0) return <span className="text-slate-400">—</span>;
  if (trips.length === 1) {
    return <div>{resized}<span className="text-amber-700">Single trip — cannot chain</span></div>;
  }
  const maxIdle = gaps.reduce((m, g) => Math.max(m, g.idle_min || 0), 0);
  if (maxIdle > 240) {
    return <div>{resized}<span className="text-amber-700">Long idle ({fmtMin(maxIdle)}) — check shift timings</span></div>;
  }
  return <div>{resized}<span className="text-emerald-700">Chained cleanly</span></div>;
}

/* ---------- data shaping ---------- */

function buildTableModel(optimizationResults) {
  if (!optimizationResults?.results?.length) {
    return { columnOrder: [], vehicles: [], summary: null };
  }
  // 1) Collect all vehicles across all zones.
  const flat = [];
  optimizationResults.results.forEach((r) => {
    const vs = r.vehicle_schedule || {};
    (vs.vehicles || []).forEach((v) =>
      flat.push({
        ...v,
        zone_name: r.entity_name,
        zone_id: r.entity_id,
        chain_detail: v.chain_detail || [],
      })
    );
  });

  // 2) Determine slot order by earliest start_min across all vehicles.
  const slotFirstStart = new Map();
  flat.forEach((v) => {
    v.chain_detail.forEach((c) => {
      if (c.kind !== "trip") return;
      const key = c.shift_slot;
      if (!slotFirstStart.has(key) || c.start_min < slotFirstStart.get(key)) {
        slotFirstStart.set(key, c.start_min);
      }
    });
  });
  const slots = Array.from(slotFirstStart.entries())
    .sort((a, b) => a[1] - b[1])
    .map(([key]) => key);

  // 3) Build column order: trip, gap-to-next, trip, gap-to-next, ...
  const columnOrder = [];
  slots.forEach((slot, i) => {
    columnOrder.push({ type: "trip", key: slot, label: slot.replace("-", " ") });
    if (i < slots.length - 1) {
      const nextSlot = slots[i + 1];
      columnOrder.push({ type: "gap", key: `${slot}__${nextSlot}` });
    }
  });

  // 4) Attach lookup tables per vehicle.
  const vehicles = flat.map((v) => {
    const by_slot = {};
    const by_gap = {};
    v.chain_detail.forEach((c) => {
      if (c.kind === "trip") by_slot[c.shift_slot] = c;
      else if (c.kind === "empty_leg") by_gap[`${c.from_slot}__${c.to_slot}`] = c;
    });
    return { ...v, by_slot, by_gap };
  });

  // 5) Sort vehicles by vehicle_id NATURALLY (B2-V1 before B10-V1, not
  //    lexicographic).  User request: table should be ordered by Vehicle
  //    column so ops can find a specific bus quickly.
  vehicles.sort((a, b) => naturalCompareVehicleId(a.vehicle_id, b.vehicle_id));

  // 6) Aggregate summary.
  const summary = {
    total_trips: 0,
    total_deadhead_min: 0,
    total_deadhead_km: 0,
    total_idle_min: 0,
    single_trip_vehicles: 0,
  };
  vehicles.forEach((v) => {
    const trips = v.chain_detail.filter((c) => c.kind === "trip");
    const gaps = v.chain_detail.filter((c) => c.kind === "empty_leg");
    summary.total_trips += trips.length;
    if (trips.length === 1) summary.single_trip_vehicles += 1;
    gaps.forEach((g) => {
      summary.total_deadhead_min += g.deadhead_min || 0;
      summary.total_deadhead_km += g.deadhead_km || 0;
      summary.total_idle_min += g.idle_min || 0;
    });
  });

  return { columnOrder, vehicles, summary };
}

/**
 * Natural comparator for vehicle_ids of the form `B<busNum>-V<vehNum>`
 * (e.g. "B2-V1", "B10-V1", "B10-V2").  Sorts by busNum numerically,
 * then vehNum numerically, so B2 < B10 < B11.  Falls back to
 * lexicographic on strings that don't match the pattern.
 */
function naturalCompareVehicleId(a, b) {
  const rx = /^B(\d+)-V(\d+)$/;
  const ma = rx.exec(a);
  const mb = rx.exec(b);
  if (ma && mb) {
    const busA = parseInt(ma[1], 10);
    const busB = parseInt(mb[1], 10);
    if (busA !== busB) return busA - busB;
    return parseInt(ma[2], 10) - parseInt(mb[2], 10);
  }
  return a.localeCompare(b);
}
